import { useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import pako from 'pako';
import { Buffer } from 'buffer';
import DatahubAPI from 'api/DatahubAPI';
import DataSearchAPI from 'api/DataSearch';
import CasesAPI from 'api/CasesAPI';
import { GlobalContext } from 'providers/GlobalProvider';
import { useLogAppAction } from 'hooks/useLogAppAction';
import { includeFields } from 'utils/OSDataHelper';
import { handleShareTicketNotification } from 'utils/NotificationsFuncs';
import { runAIEvaluationSingleProduct } from 'components/Screening/searchComponents/AIEvaluate';
import {
  AiEvaluation,
  EvalState,
  HtsCatalogRow,
  ListCatalogRow,
  MatchRow,
  ParsedQuery,
  Phase,
  RecentScreen,
  RunInput,
  SavedFilter,
  SavedFilterKind,
  Scope,
} from './types';
import { Jurisdiction, ScreeningProfile, routeCountries } from './profiles';
import {
  AI_ERROR_MARKER,
  EVAL_LIMIT,
  NOMENCLATURE_INDEX,
  buildScreenPayload,
  flattenHtsAggs,
  flattenListAggs,
  htsCodeLists,
  isoToday,
  parseQuery,
  queryTerm,
  readRecents,
  savedFields,
  toMatchRows,
  writeRecent,
} from './utils';

interface LastRun extends RunInput {
  parsed: ParsedQuery;
  at: number;
}

const compress = (data: any) =>
  Buffer.from(pako.gzip(JSON.stringify(data))).toString('base64');

const evaluationDate = () => moment().format('MM/DD/YYYY');

const notEvaluated = (explanation: string, hsCode = ''): AiEvaluation => ({
  hs_code: hsCode,
  explanation: explanation,
  risk_level: '',
  burden: '',
  trade_category: '',
  key_factors: [],
  screenDate: evaluationDate(),
});

export interface ProductScreening {
  listRows: ListCatalogRow[];
  htsRows: HtsCatalogRow[];
  codeLists: { key: string; n: number }[];
  recordTotal: number | null;
  catalogLoading: boolean;
  loadCatalog: (jurisdiction: Jurisdiction) => Promise<void>;
  savedFilters: SavedFilter[];
  createSavedFilter: (
    kind: SavedFilterKind,
    name: string,
    s: Scope,
  ) => Promise<SavedFilter | null>;
  updateSavedFilter: (f: SavedFilter, s: Scope) => Promise<void>;
  deleteSavedFilter: (f: SavedFilter) => Promise<void>;
  phase: Phase;
  secs: number;
  rows: MatchRow[];
  total: number;
  serverPage: number;
  evalState: EvalState;
  lastRun: LastRun | null;
  screenError: string;
  run: (
    input: RunInput,
    pageNum?: number,
    opts?: { pageOnly?: boolean },
  ) => Promise<void>;
  retryEvaluation: () => Promise<void>;
  recents: RecentScreen[];
  /** Saving over a ticket draft opens the ticket afterwards unless `redirect` is false. */
  saveTicket: (
    name: string,
    shareWith: string[],
    frequency: string,
    opts?: { redirect?: boolean },
  ) => Promise<boolean>;
  saving: boolean;
  savedTicket: { id: string; freq: string } | null;
}

export const useProductScreening = (
  profile: ScreeningProfile,
  enabled: boolean,
): ProductScreening => {
  const { direction, index } = profile;
  const logKey = `${direction.toLowerCase()}_screening`;
  const { cur_user, ticketDraft, setTicketDraft } = useContext(GlobalContext);
  const { enqueueSnackbar } = useSnackbar();
  const history = useHistory();
  const logAppAction = useLogAppAction();
  const teamId: string = cur_user?.selected_team?.id || '';

  const [listRows, setListRows] = useState<ListCatalogRow[]>([]);
  const [htsRows, setHtsRows] = useState<HtsCatalogRow[]>([]);
  const [codeLists, setCodeLists] = useState<{ key: string; n: number }[]>([]);
  const [recordTotal, setRecordTotal] = useState<number | null>(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const catalogRequestId = useRef(0);

  const loadCatalog = useCallback(
    async (jurisdiction: Jurisdiction) => {
      if (!enabled) {
        return;
      }
      catalogRequestId.current += 1;
      const requestId = catalogRequestId.current;
      setCatalogLoading(true);
      try {
        const [aggRes, htsRes] = await Promise.all([
          DatahubAPI.commoditySearch({
            indices: [index],
            tradeType: jurisdiction.tradeType,
            type: direction,
            agg: true,
          }),
          DatahubAPI.commoditySearch({
            indices: [NOMENCLATURE_INDEX],
            type: direction,
            agg: true,
          }),
        ]);

        if (requestId !== catalogRequestId.current) {
          return;
        }

        const aggBody = aggRes?.success
          ? aggRes.result?.result?.body
          : null;
        setListRows(flattenListAggs(aggBody?.aggregations));
        setRecordTotal(
          typeof aggBody?.hits?.total?.value === 'number'
            ? aggBody.hits.total.value
            : null,
        );

        const htsBody = htsRes?.success
          ? htsRes.result?.result?.body
          : null;
        setHtsRows(flattenHtsAggs(htsBody?.aggregations));
        setCodeLists(htsCodeLists(htsBody?.aggregations));
      } catch (_error) {
        if (requestId === catalogRequestId.current) {
          setListRows([]);
          setHtsRows([]);
          setCodeLists([]);
          setRecordTotal(null);
        }
      } finally {
        if (requestId === catalogRequestId.current) {
          setCatalogLoading(false);
        }
      }
    },
    [enabled, index, direction],
  );

  const [savedFilters, setSavedFilters] = useState<SavedFilter[]>([]);

  useEffect(() => {
    if (!enabled || !teamId) {
      return undefined;
    }
    let live = true;
    DataSearchAPI.getUserSearchFilters({
      query: { groupId: teamId, userId: cur_user?.basics?.id },
    }).then((res: any) => {
      if (live && res?.success && Array.isArray(res.result)) {
        setSavedFilters(
          res.result.filter(
            (f: any) =>
              f.type === 'commodityList' || f.type === 'commodityCode',
          ),
        );
      }
    });
    return () => {
      live = false;
    };
  }, [enabled, teamId, cur_user?.basics?.id]);

  const filterBody = (kind: SavedFilterKind, s: Scope) => ({
    type: kind,
    ...savedFields(s, kind),
    entRes: true,
  });

  const createSavedFilter = async (
    kind: SavedFilterKind,
    name: string,
    s: Scope,
  ): Promise<SavedFilter | null> => {
    const res: any = await DataSearchAPI.createSearchFilter({
      newSearchFilter: {
        userId: cur_user?.basics?.id,
        groupId: teamId,
        filterName: name,
        ...filterBody(kind, s),
      },
    });
    if (res?.success) {
      setSavedFilters((arr) => [...arr, res.result]);
      enqueueSnackbar('Saved your filter', { variant: 'success' });
      return res.result as SavedFilter;
    }
    enqueueSnackbar('Could not save the filter. Please try again.', {
      variant: 'error',
    });
    return null;
  };

  const updateSavedFilter = async (f: SavedFilter, s: Scope) => {
    const res: any = await DataSearchAPI.updateSearchFilter({
      update: { ...f, ...filterBody(f.type, s) },
    });
    if (res?.success) {
      setSavedFilters((arr) =>
        arr.map((x) => (x._id === res.result._id ? res.result : x)),
      );
      enqueueSnackbar(`Updated “${f.filterName}”`, { variant: 'success' });
    } else {
      enqueueSnackbar('Could not update the filter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const deleteSavedFilter = async (f: SavedFilter) => {
    const res: any = await DataSearchAPI.deleteSearchFilter(f._id);
    if (res?.success) {
      setSavedFilters((arr) => arr.filter((x) => x._id !== f._id));
    } else {
      enqueueSnackbar('Could not delete the filter. Please try again.', {
        variant: 'error',
      });
    }
  };

  const [phase, setPhase] = useState<Phase>('idle');
  const [secs, setSecs] = useState(0);
  const [rows, setRows] = useState<MatchRow[]>([]);
  const [total, setTotal] = useState(0);
  const [hits, setHits] = useState<any[]>([]);
  const [evalState, setEvalState] = useState<EvalState>({
    status: 'none',
    missing: 'both',
  });
  const [lastRun, setLastRun] = useState<LastRun | null>(null);
  const [serverPage, setServerPage] = useState(0);
  const [screenError, setScreenError] = useState('');
  const [recents, setRecents] = useState<RecentScreen[]>(() =>
    readRecents(profile, teamId),
  );
  const runId = useRef(0);
  const tick = useRef<number | null>(null);

  useEffect(() => {
    setRecents(readRecents(profile, teamId));
  }, [profile, teamId]);

  useEffect(
    () => () => {
      if (tick.current) {
        window.clearInterval(tick.current);
      }
    },
    [],
  );

  const evaluate = useCallback(
    async (
      id: number,
      input: RunInput,
      parsed: ParsedQuery,
      hitList: any[],
      count: number,
    ) => {
      const missingHts = parsed.kind !== 'hts';
      const missingPartner = !input.partner;
      if (missingHts || missingPartner) {
        setEvalState({
          status: 'none',
          missing:
            missingHts && missingPartner
              ? 'both'
              : missingHts
              ? 'hts'
              : 'partner',
        });
        return null;
      }
      if (count > EVAL_LIMIT || hitList.length > EVAL_LIMIT) {
        setEvalState({ status: 'skipped' });
        return null;
      }
      setEvalState({ status: 'wait' });
      const sources = hitList.map((h: any) => h._source);
      const filtered = await includeFields(
        [
          'HS Code',
          'BITE Lens',
          'BITE-Identified Risk Country',
          'List Name',
          'Trade Type',
        ],
        sources,
      );
      const seen = new Set<string>();
      const unique = filtered.filter((item: any) => {
        const name = item['List Name'];
        if (!name || seen.has(name)) {
          return false;
        }
        seen.add(name);
        return true;
      });
      const route = routeCountries(profile, input.jurisdiction, input.partner);
      const res = (await runAIEvaluationSingleProduct(
        parsed.text,
        route.origin,
        route.destination,
        JSON.stringify(unique),
        direction,
        teamId,
        'screening',
      )) as AiEvaluation | null;
      if (id !== runId.current) {
        return null;
      }
      if (!res || res.hs_code === AI_ERROR_MARKER) {
        setEvalState({ status: 'error' });
        return null;
      }
      const data: AiEvaluation = {
        ...res,
        key_factors: Array.isArray(res.key_factors) ? res.key_factors : [],
        screenDate: res.screenDate || evaluationDate(),
      };
      setEvalState({ status: 'done', data: data });
      return data;
    },
    [profile, direction],
  );

  const run = useCallback(
    async (input: RunInput, pageNum = 0, opts: { pageOnly?: boolean } = {}) => {
      if (!enabled) {
        return;
      }
      const parsed = parseQuery(input.q);
      if (parsed.kind === 'badhts') {
        return;
      }
      runId.current += 1;
      const id = runId.current;
      setScreenError('');
      if (!opts.pageOnly) {
        logAppAction(`${logKey}:run`);
        setPhase('run');
        setSecs(0);
        setRows([]);
        setHits([]);
        setEvalState({ status: 'wait' });
        if (tick.current) {
          window.clearInterval(tick.current);
        }
        const started = Date.now();
        tick.current = window.setInterval(() => {
          setSecs(Math.floor((Date.now() - started) / 1000));
        }, 1000);
      }
      let res: any = null;
      try {
        res = await DatahubAPI.commoditySearch(
          buildScreenPayload({
            ...input,
            index: index,
            direction: direction,
            parsed: parsed,
            pageNum: pageNum,
          }),
        );
      } catch (_error) {
        // Treat transport failures like unsuccessful API responses below.
      }
      if (id !== runId.current) {
        return;
      }
      if (tick.current && !opts.pageOnly) {
        window.clearInterval(tick.current);
        tick.current = null;
      }
      const body = res?.success ? res.result?.result?.body : null;
      if (!body) {
        setEvalState({ status: 'error' });
        setScreenError(
          'The screen did not complete. Nothing was changed; run it again.',
        );
        setPhase(opts.pageOnly ? 'done' : lastRun ? 'done' : 'idle');
        if (!opts.pageOnly) {
          enqueueSnackbar('The screen did not complete. Please run it again.', {
            variant: 'error',
          });
        }
        return;
      }
      const hitList: any[] = body.hits?.hits || [];
      const count = Number(body.hits?.total?.value) || hitList.length;
      setHits(hitList);
      setRows(toMatchRows(hitList));
      setTotal(count);
      setServerPage(pageNum);
      setPhase('done');
      if (opts.pageOnly) {
        return;
      }
      const at = Date.now();
      setLastRun({ ...input, parsed: parsed, at: at });
      const data = await evaluate(id, input, parsed, hitList, count);
      if (id !== runId.current) {
        return;
      }
      if (parsed.kind !== 'empty') {
        setRecents(
          writeRecent(profile, teamId, {
            q: queryTerm(parsed),
            partner: input.partner,
            jurisdiction: input.jurisdiction.id,
            matches: count,
            risk: data?.risk_level || '',
            at: at,
          }),
        );
      }
    },
    [
      enabled,
      evaluate,
      logAppAction,
      logKey,
      profile,
      index,
      direction,
      teamId,
      enqueueSnackbar,
      lastRun,
    ],
  );

  const retryEvaluation = useCallback(async () => {
    if (!lastRun) {
      return;
    }
    runId.current += 1;
    await evaluate(runId.current, lastRun, lastRun.parsed, hits, total);
  }, [evaluate, hits, lastRun, total]);

  const [savedTicket, setSavedTicket] = useState<{
    id: string;
    freq: string;
    // The ticket's audit log as the last save returned it.
    activity?: any[];
  } | null>(null);
  const [saving, setSaving] = useState(false);

  const ticketEvaluation = (): AiEvaluation => {
    const partnerKey = profile.partner.key;
    switch (evalState.status) {
      case 'done':
        return evalState.data as AiEvaluation;
      case 'skipped':
        return notEvaluated(
          `Not evaluated. The screen found more than ${EVAL_LIMIT.toLocaleString()} matches; narrow it with filters or a longer HTS code to get an evaluation.`,
          lastRun ? queryTerm(lastRun.parsed) : '',
        );
      case 'error':
        return notEvaluated(
          'Not evaluated. The evaluation service did not respond.',
          AI_ERROR_MARKER,
        );
      case 'wait':
        return notEvaluated(
          'Not evaluated. The ticket was saved before the evaluation finished.',
        );
      default:
        return notEvaluated(
          evalState.missing === 'hts'
            ? 'Not evaluated. The evaluation needs an HTS code.'
            : evalState.missing === 'partner'
            ? `Not evaluated. The evaluation needs a country of ${partnerKey}.`
            : `Not evaluated. The evaluation needs an HTS code and a country of ${partnerKey}.`,
        );
    }
  };

  const saveTicket = async (
    name: string,
    shareWith: string[],
    frequency: string,
    opts: { redirect?: boolean } = {},
  ) => {
    if (!lastRun || !enabled) {
      return false;
    }
    logAppAction(`${logKey}:save`);
    setSaving(true);
    const today = isoToday();
    const { parsed, scope, jurisdiction, partner, asOf } = lastRun;
    const route = routeCountries(profile, jurisdiction, partner);
    const regionStamp = jurisdiction.stampsTicket
      ? { tradeType: jurisdiction.tradeType, tradeRegion: jurisdiction.region }
      : {};
    const hsCode = parsed.kind === 'hts' ? parsed.fmt : '';
    const desc = parsed.kind === 'desc' ? parsed.text : '';
    const owner = {
      kc_id: cur_user.basics?.id,
      email: cur_user.basics?.email,
      firstName: cur_user.basics?.firstName,
      lastName: cur_user.basics?.lastName,
    };
    const aiSums = [ticketEvaluation()];
    const query: any = {
      ticketName: name,
      lastUpdate: today,
      commodity: {
        harmonizedScheduleCode: hsCode,
        commodityName: desc,
        countryOfOrigin: route.origin,
        destinationCountry: route.destination || 'USA',
        ...regionStamp,
      },
      docType: `${direction} Product Screen`,
      commodityFilter: {
        ...savedFields(scope),
        entRes: true,
        filterDate: asOf.mode === 'range' ? [asOf.from, asOf.to] : [],
        ...(jurisdiction.stampsTicket
          ? {
              // `tradeType` is retained for tickets created before scheduled
              // re-screens used the plural OpenSearch filter below.
              tradeType: jurisdiction.tradeType,
              tradeTypes: [jurisdiction.tradeType],
            }
          : {}),
      },
      entityFilter: {
        lenses: [],
        countries: [],
        agencies: [],
        lists: [],
        entRes: false,
        filterDate: [],
      },
      frequency: frequency,
      sharedWith: shareWith,
      aiSums: aiSums,
      activeFilter: '',
      selectedOpts: [],
      status: 'Complete',
      favorite: false,
      groupName: cur_user.selected_team?.name,
      template: [],
      colMapping: [],
      activity: { user: owner, action: 'saved work', timeStamp: Date.now() },
      owner: owner,
      curTotal: total,
      curNew: 0,
      results: {
        allResults: compress([
          {
            dataSet: 'Commodities',
            docs: hits.map((h: any) => h._source),
            field: queryTerm(parsed),
            hits: total,
            row: 1,
          },
        ]),
        allTotal: total,
        newResults: [],
        newTotal: 0,
        date: today,
        docInfo: [],
      },
    };

    // A newly saved screen has no ticketDraft, so use the ticket id we just
    // created when the user opens the Save popover again. Otherwise every
    // subsequent save would create a duplicate Saved Work ticket.
    const existingTicket = ticketDraft?._id
      ? ticketDraft
      : savedTicket?.id
      ? { _id: savedTicket.id, activity: savedTicket.activity }
      : null;
    // An update replaces the ticket's whole audit log, so a re-save keeps the
    // earlier entries and adds this save after them.
    const priorActivity = existingTicket?.activity;
    const activity = [
      ...(Array.isArray(priorActivity) ? priorActivity : []),
      query.activity,
    ];
    const resp: any = existingTicket
      ? await CasesAPI.updateCase({
          ...existingTicket,
          ...query,
          activity: activity,
          groupId: teamId,
        })
      : await CasesAPI.createCase({
          newCase: {
            ...query,
            groupId: teamId,
          },
        });
    setSaving(false);
    if (resp?.success) {
      const caseId = resp.result?._id || existingTicket?._id;
      shareWith.forEach((email) =>
        handleShareTicketNotification(name, cur_user, email, caseId),
      );
      setSavedTicket({
        id: caseId,
        freq: frequency,
        activity: Array.isArray(resp.result?.activity)
          ? resp.result.activity
          : activity,
      });
      enqueueSnackbar('Saved to Saved Work', { variant: 'success' });
      if (ticketDraft?._id) {
        setTicketDraft({});
        if (opts.redirect !== false) {
          history.push({ pathname: `/monitor/ticket/${caseId}` });
        }
      }
      return true;
    }
    enqueueSnackbar(
      'Could not save the ticket. The results may be too large to store.',
      {
        variant: 'error',
      },
    );
    return false;
  };

  useEffect(() => {
    setSavedTicket(null);
  }, [lastRun]);

  return {
    listRows: listRows,
    htsRows: htsRows,
    codeLists: codeLists,
    recordTotal: recordTotal,
    catalogLoading: catalogLoading,
    loadCatalog: loadCatalog,
    savedFilters: savedFilters,
    createSavedFilter: createSavedFilter,
    updateSavedFilter: updateSavedFilter,
    deleteSavedFilter: deleteSavedFilter,
    phase: phase,
    secs: secs,
    rows: rows,
    total: total,
    serverPage: serverPage,
    evalState: evalState,
    lastRun: lastRun,
    screenError: screenError,
    run: run,
    retryEvaluation: retryEvaluation,
    recents: recents,
    saveTicket: saveTicket,
    saving: saving,
    savedTicket: savedTicket,
  };
};
